import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * SyncBook — Privacy Policy
 *
 * Public, unauthenticated page. Referenced from:
 *  - Twilio A2P 10DLC campaign registration (privacyPolicyUrl)
 *  - Footer link on the auth screens
 *  - Profile → SMS section (right next to the opt-in checkbox)
 *
 * If material sections of the app change (new data collected, new sharing,
 * change of data-processor sub-processors), bump `Last updated` below.
 */
export default function Privacy() {
  return (
    <div
      className="max-w-3xl mx-auto p-5 space-y-5 pb-16"
      data-testid="privacy-page"
    >
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
          data-testid="privacy-back"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Privacy Policy</h1>
        <div className="w-[70px]" />
      </div>

      <p className="text-xs font-medium text-[#4a4a4a]">
        Last updated: February 22, 2026
      </p>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Who we are</h2>
        <p>
          SyncBook (&quot;we&quot;, &quot;us&quot;) is a personal-finance app
          that helps teenagers balance their checkbook. This Privacy Policy
          explains what data we collect when you use{" "}
          <span className="font-black">sync-book.app</span>, how we use it, and
          the choices you have. If any of this isn&apos;t clear, email us and
          we&apos;ll answer.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">What we collect</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <b>Account info</b> — your name, email, and password (stored hashed,
            never in plain text). Optional: phone number if you enable SMS
            logging.
          </li>
          <li>
            <b>Financial data</b> — the transactions, bank balances, budgets,
            savings goals, and recurring bills/paychecks you enter yourself or
            import from CSVs. We do <b>not</b> connect to your bank.
          </li>
          <li>
            <b>Receipt images</b> — photos you snap for OCR. Stored on our
            servers linked to that transaction and never used for anything else.
            You can delete them from the transaction detail screen.
          </li>
          <li>
            <b>Usage & device</b> — basic web-server logs (IP address, browser
            version, timestamps) kept 30 days for security and debugging.
          </li>
          <li>
            <b>Payment info</b> (if you subscribe to a paid tier) — handled
            entirely by Stripe. We never see or store your card number; we only
            store your Stripe customer ID and subscription status.
          </li>
        </ul>
      </section>

      <section
        className="nb-card p-5 space-y-3 text-sm"
        data-testid="privacy-sms-section"
      >
        <h2 className="font-display font-black text-lg">
          SMS logging (opt-in)
        </h2>
        <p>
          If you enable SMS in Profile → SMS, you consent to us sending
          transactional replies to your phone via Twilio when you text logging
          shorthands (e.g. <span className="font-mono">-4.50 coffee</span>).
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Message frequency varies by your usage — typically 1–5 msgs/day.</li>
          <li>
            Standard message & data rates from your carrier may apply. SyncBook
            does not charge you for SMS.
          </li>
          <li>
            Reply <b>STOP</b> at any time to unsubscribe, or unlink your phone
            from Profile → SMS.
          </li>
          <li>
            Reply <b>HELP</b> to see supported commands. Or visit{" "}
            <Link className="underline font-bold" to="/help/sms">
              /help/sms
            </Link>{" "}
            in-app.
          </li>
          <li>
            Your phone number is <b>never</b> sold, rented, or shared with
            third parties, affiliates, or marketing partners for marketing,
            promotional, or lead-generation purposes. It is shared
            only with Twilio (our SMS carrier) to deliver your reply, and is
            stored solely to match inbound texts to your account.
          </li>
          <li>
            SMS content is limited to transaction confirmations, balance
            summaries, and STOP/HELP replies. We never send promotional,
            marketing, or third-party content over SMS.
          </li>
          <li>
            <b>SMS opt-in is not a condition of using SyncBook or purchasing
            any subscription.</b> Every SyncBook feature (transactions,
            budgets, reconcile, forecasting, imports, receipts) is available
            without providing a phone number.
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">How we use your data</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To operate the core features you interact with (register, forecast, reconcile, etc.).</li>
          <li>To send transactional SMS you explicitly opted into.</li>
          <li>
            To detect abuse (rate limits, brute-force login attempts) and keep
            your account secure.
          </li>
          <li>
            To send occasional service emails — password resets, subscription
            receipts, weekly Money Health digest (opt-in).
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Who we share with</h2>
        <p>
          We do <b>not</b> sell your data. We share only what&apos;s strictly
          needed to operate the app, with these sub-processors:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <b>Twilio</b> — SMS delivery. Sees your phone number and the text
            content.
          </li>
          <li>
            <b>Stripe</b> — payment processing for Pro / Family subscriptions.
            Sees your email and payment method.
          </li>
          <li>
            <b>SendGrid</b> — transactional email (password resets, receipts).
            Sees your email.
          </li>
          <li>
            <b>OpenAI (via Emergent LLM Key)</b> — receipt-image OCR and CSV
            payee cleanup. Sees receipt images and short bank-line descriptions.
            Content is not used for model training.
          </li>
          <li>
            <b>MongoDB Atlas</b> — encrypted database hosting.
          </li>
        </ul>
        <p>
          Each of these has its own privacy policy and data-processing agreement
          in place. We share no data with anyone else.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Your rights</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <b>Access</b> — view all your data in-app under Profile & Register.
          </li>
          <li>
            <b>Correction</b> — edit any transaction, budget, or account at any time.
          </li>
          <li>
            <b>Deletion</b> — delete your account from Profile → Delete Account.
            This removes all your transactions, receipts, budgets, and personal
            info within 30 days. Server logs are purged on their normal 30-day
            rotation.
          </li>
          <li>
            <b>Portability</b> — export your transactions to CSV from the
            Register page (feature launching soon).
          </li>
          <li>
            <b>SMS opt-out</b> — text <b>STOP</b> to +1 417 962 8253 or unlink
            your phone in Profile → SMS.
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Users under 13</h2>
        <p>
          SyncBook is designed for users 13 and older. We do not knowingly
          collect data from children under 13. If you believe a child under 13
          has created an account, please email us and we will delete it.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Data retention</h2>
        <p>
          We keep your data as long as your account is active. After account
          deletion, we retain minimal records (e.g. Stripe receipts) only as
          long as legally required, then permanently delete them.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Changes to this policy</h2>
        <p>
          If we make material changes, we&apos;ll notify signed-in users in-app
          before the change takes effect. Small clarifications will only bump
          the &quot;Last updated&quot; date at the top.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">Contact</h2>
        <p>
          Privacy questions, data requests, or SMS complaints:{" "}
          <a
            className="underline font-bold"
            href="mailto:support@sync-book.app"
          >
            support@sync-book.app
          </a>
          .
        </p>
      </section>

      <div className="text-center text-xs font-medium text-[#4a4a4a] pt-2">
        <Link className="underline" to="/terms" data-testid="privacy-to-terms">
          Terms of Service
        </Link>
      </div>
    </div>
  );
}
